import { CommonModule, NgClass } from '@angular/common';
import {
  Component,
  ContentChild,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  inject,
  Input,
  TemplateRef,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
  QueryList,
  ViewChildren,
  SimpleChanges,
  OnInit,
  OnChanges,
  PLATFORM_ID,
} from '@angular/core';
import { PackageCardViewComponent } from '../../cards/package-card-view/package-card-view.component';
import { Store } from '@ngrx/store';
import { selectAllPackageThemes } from '../../../../components/home/redux/selectors/home.selectors';
import { SectionTitleComponent } from '../../ui-elements/section-title/section-title.component';
import { Router, RouterModule } from '@angular/router';
import { SwiperOptions } from 'swiper/types';
import { CarouselNavigationComponent } from '../../carousels/carousel-navigation/carousel-navigation.component';
import { DestinationCardComponent } from '../../cards/destination-card/destination-card.component';
import { NoResultFoundComponent } from '../../no-result-found/no-result-found.component';
import { SwiperExtensionDirective } from '../../../directives/swiper-extension.directive';
import { MatIconModule } from '@angular/material/icon';
import { ButtonComponent } from '../../buttons/button/button.component';
import { ViewportService } from '../../../../components/services/viewport.service';
import { IndianCurrencyPipe } from '../../../pipes/indian-currency.pipe';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-expanded-themes',
  standalone: true,
  imports: [
    NgClass,
    PackageCardViewComponent,
    SectionTitleComponent,
    CarouselNavigationComponent,
    DestinationCardComponent,
    NoResultFoundComponent,
    SwiperExtensionDirective,
    CommonModule,
    ButtonComponent,
    IndianCurrencyPipe,
    RouterModule,
  ],
  templateUrl: './expanded-themes.component.html',
  styleUrls: ['./expanded-themes.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ExpandedThemesComponent
  implements AfterViewInit, OnInit, OnChanges
{
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private store = inject(Store);
  private viewportService = inject(ViewportService);
  private platformId = inject(PLATFORM_ID);

  @ViewChild('swiperCardsContainer', { static: false })
  swiperContainer!: ElementRef | null;
  swiperInstance!: any;
  @ViewChildren('panel') panels!: QueryList<ElementRef>;
  @ContentChild('navigationLeftContent')
  navigationLeftContent!: TemplateRef<any>;

  @Input() navigationLeftContentVisible: boolean = true;
  @Input() hidePricing = false;
  @Input() isLoading = true;

  themes: any[] = []; // now normal array
  currentIndex: number = 0;
  isBrowser: boolean = false;
  hideExploreNowBtn: boolean = false;

  category = 'themes';

  breakpoints: any = {
    320: { slidesPerView: 3.8, spaceBetween: 0 },
    340: { slidesPerView: 3.8, spaceBetween: 5 },
    360: { slidesPerView: 3.8, spaceBetween: 25 },
    375: { slidesPerView: 3.8, spaceBetween: 20 },
    390: { slidesPerView: 3.8, spaceBetween: 40 },
    414: { slidesPerView: 3.6, spaceBetween: 10 },
    425: { slidesPerView: 3.7, spaceBetween: 25 },
    480: { slidesPerView: 3.8, spaceBetween: 10 },
    540: { slidesPerView: 3.4, spaceBetween: 0 },
    640: { slidesPerView: 3.2, spaceBetween: 15 },
    768: { slidesPerView: 3.4, spaceBetween: 30 },
    820: { slidesPerView: 3.5, spaceBetween: 20 },
    1024: { slidesPerView: 4.7, spaceBetween: 20 },
    1180: { slidesPerView: 5.6, spaceBetween: 40 },
    1440: { slidesPerView: 5.3, spaceBetween: 30 },
    2560: { slidesPerView: 5.3, spaceBetween: 30 },
    loopAdditionalSlides: 0,
    centerInsufficientSlides: true,
  };

  swiperConfig: SwiperOptions = {
    init: true,
    slidesPerView: 2,
    loop: false,
    spaceBetween: 10,
    centeredSlides: false,
    mousewheel: { forceToAxis: true },
    breakpoints: this.breakpoints,
    watchOverflow: true,
  };

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.store.select(selectAllPackageThemes).subscribe((state) => {
      if (state.packageThemes?.length > 0) {
        this.themes = state.packageThemes;
        this.setMiddleThemeAsDefault();
        this.cdr.detectChanges(); // manually trigger after themes change
      }
      this.viewportService.getViewportWidth().subscribe((width: number) => {
        this.hideExploreNowBtn = width < 768;
      });
    });
  }

  ngAfterViewInit(): void {
    if (this.isBrowser && this.swiperContainer) {
      setTimeout(() => {
        this.initSwiper();
        this.cdr.detectChanges();
      }, 0);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {}

  initSwiper() {
    if (this.isBrowser && this.swiperContainer) {
      Object.assign(this.swiperContainer.nativeElement, this.swiperConfig);
      this.swiperContainer.nativeElement.initialize();
      this.swiperInstance = this.swiperContainer.nativeElement.swiper;
    }
  }

  setMiddleThemeAsDefault() {
    if (!this.isBrowser) return;
    if (!this.themes.length) return;

    const applyTheme = () => {
      this.currentIndex =
        window.innerWidth <= 480 ? 1 : window.innerWidth <= 1024 ? 1 : 2;
      document.querySelectorAll('.panel').forEach((panel, index) => {
        panel.classList.toggle('active', index === this.currentIndex);
      });
    };

    applyTheme();
    window.addEventListener('resize', applyTheme);
  }

  modifiedSlug(slug: string) {
    const modSlug = slug?.toLowerCase();
    return `${modSlug}`;
  }

  onPanelClick(index: number): void {
    this.currentIndex = index;
  }
  redirectToTheme(theme: any) {
    const slugPath = this.modifiedSlug(theme);
    this.router.navigate(['/', slugPath], {
      state: {
        theme: theme?._id,
        sortBy: 'price',
        sortOrder: 'asc',
        page: 1,
        limit: 15,
      },
    });
  }

  removeActiveClasses() {
    this.currentIndex = -1;
  }
}
